import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { Observable, of, forkJoin } from "rxjs";
import { map, tap, first, catchError, switchMap } from "rxjs/operators";
import { AuthService } from "@auth0/auth0-angular";
import { localStorageKey } from "@core/constants";
import { PermissionValidationService } from "@core/services/permission-validation.service";

@Injectable()
export class PermissionGuard {
  constructor(
    protected router: Router,
    public auth: AuthService,
    private permissionValidationService: PermissionValidationService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const hasLocalStorageData = () => {
      try {
        const data = localStorage.getItem(localStorageKey);
        return data && JSON.parse(data)?.user && JSON.parse(data)?.tokenClaims;
      } catch {
        return false;
      }
    };

    const getUserData = () => {
      try {
        const data = localStorage.getItem(localStorageKey);
        return data ? JSON.parse(data)?.user : null;
      } catch {
        return null;
      }
    };

    return this.auth.isAuthenticated$.pipe(
      first(),
      map((isAuthenticated) => {
        if (!isAuthenticated && hasLocalStorageData()) {
          return true;
        }
        return isAuthenticated;
      }),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          return of(false);
        }

        this.permissionValidationService.initialize();

        const userData = getUserData();
        const permissionsAllowed = route?.data?.roles as string[];

        if (!permissionsAllowed || permissionsAllowed.length === 0) {
          return of(true);
        }

        return this.permissionValidationService.waitForLoad().pipe(
          switchMap(() => {
            const permissionChecks = permissionsAllowed.map((permission) => {
              const userHasPermission = userData?.permissions?.includes(permission) || false;
              return this.permissionValidationService.canAccess(permission, userHasPermission);
            });

            return forkJoin(permissionChecks).pipe(
              map((results) => results.some((result) => result === true))
            );
          })
        );
      }),
      tap((canAccess) => {
        if (!canAccess) {
          this.router.navigate(["/dashboard/home"]);
        }
      }),
      catchError(() => {
        if (hasLocalStorageData()) {
          const userData = getUserData();
          const permissionsAllowed = route?.data?.roles as string[];

          if (!permissionsAllowed || permissionsAllowed.length === 0) {
            return of(true);
          }

          const hasAnyPermission = permissionsAllowed.some((permission) => {
            const userHasPermission = userData?.permissions?.includes(permission) || false;
            return this.permissionValidationService.canAccessSync(permission, userHasPermission);
          });

          if (!hasAnyPermission) {
            this.router.navigate(["/dashboard/home"]);
          }
          return of(hasAnyPermission);
        }
        this.router.navigate(["/dashboard/home"]);
        return of(false);
      })
    );
  }
}
